import React, { useContext } from "react";
import { DataLayerContext } from "../dataLayer/DataLayer";
import "./reviewPuzzleScreen.css";
import { AppBar } from "./components/appBar";
import { Share as ShareIcon, Question as InfoIcon } from "./components/icons";
import { getDayName, SimpleDate } from "../dataLayer/SimpleDate";
import { useObservableProperty } from "../observable/useObservableProperty";
import { IPuzzleV1 } from "../dataLayer/v1/interfaces/classes/IPuzzleV1";
import { PuzzleReviewBoard } from "./components/puzzleReviewBoard";
import { ReviewRareWords } from "./components/reviewRareWords";
import { ReviewAllWords } from "./components/reviewAllWords";
import { IWordInPuzzleV1 } from "../dataLayer/v1/interfaces/classes/IWordInPuzzleV1";
import { generateShareResultsText } from "../helpers/generateShareResultsText";

export interface IReviewPuzzleScreenProps {
  puzzle: IPuzzleV1;
}

function longDate(d: SimpleDate): string {
  return new Date(d.year, d.month - 1, d.day).toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

export function ReviewPuzzleScreen(props: IReviewPuzzleScreenProps) {
  const dataLayer = useContext(DataLayerContext);

  const allWords = useObservableProperty<IWordInPuzzleV1[]>(props.puzzle.allWords || []);
  const [goldWords, setGoldWords] = React.useState<string[]>([]);
  const [silverWords, setSilverWords] = React.useState<string[]>([]);
  const [bronzeWords, setBronzeWords] = React.useState<string[]>([]);
  const [selectedWord, setSelectedWord] = React.useState<string | undefined>();
  const totalScore = useObservableProperty<number>(props.puzzle.totalScore);
  const rarityPoints = useObservableProperty<number | undefined>(props.puzzle.rarityPoints);
  const regularPoints = useObservableProperty<number | undefined>(props.puzzle.regularPoints);
  const [foundWordsCount, setFoundWordsCount] = React.useState<number>(0);
  const [foundCountByLength, setFoundCountByLength] = React.useState<number[]>([]);
  const showRarityInfo = useObservableProperty(dataLayer.currentOverlay) === "rarity";

  const [wordsToDisplayByLength, setWordsToDisplayByLength] = React.useState<IWordInPuzzleV1[][]>(
    [],
  );

  React.useEffect(() => {
    let _foundWordsCount = 0;
    const _foundCountByLength: number[] = [];
    let goldWordList: string[] = [];
    let silverWordList: string[] = [];
    let bronzeWordList: string[] = [];
    const _wordsToDisplayByLength: IWordInPuzzleV1[][] = [];
    allWords.forEach((value: IWordInPuzzleV1) => {
      if (value.isFound) {
        _foundWordsCount++;
        _foundCountByLength[value.word.length] = (_foundCountByLength[value.word.length] || 0) + 1;
        switch (value.rarityLevel) {
          case "Gold":
            goldWordList.push(value.word);
            break;
          case "Silver":
            silverWordList.push(value.word);
            break;
          case "Bronze":
            bronzeWordList.push(value.word);
            break;
        }
      }
      if (value.isFound || !props.puzzle.isToday) {
        if (_wordsToDisplayByLength[value.word.length] === undefined) {
          _wordsToDisplayByLength[value.word.length] = [value];
        } else {
          _wordsToDisplayByLength[value.word.length].push(value);
        }
      }
    });
    for (let wordLength = 0; wordLength <= _wordsToDisplayByLength.length; wordLength++) {
      if (_wordsToDisplayByLength[wordLength]) {
        _wordsToDisplayByLength[wordLength].sort((a: IWordInPuzzleV1, b: IWordInPuzzleV1) =>
          a.word < b.word ? -1 : 1,
        );
      }
    }
    setFoundWordsCount(_foundWordsCount);
    setFoundCountByLength(_foundCountByLength);
    setGoldWords(goldWordList);
    setSilverWords(silverWordList);
    setBronzeWords(bronzeWordList);
    setWordsToDisplayByLength(_wordsToDisplayByLength);
  }, [allWords]);

  const onBackClicked = () => {
    dataLayer.currentPuzzle.value = undefined;
    dataLayer.navigateBack();
  };

  if (dataLayer.currentPuzzle.value === undefined) {
    dataLayer.navigateBack();
    return <></>;
  }

  const onShareClicked = () => {
    navigator
      .share({
        text: generateShareResultsText(
          props.puzzle,
          wordsToDisplayByLength,
          foundWordsCount,
          regularPoints,
          rarityPoints,
          totalScore,
        ),
      })
      .catch((e) => console.warn("Share threw exception:", e));
  };

  const goldCount = goldWords.length;
  const silverCount = silverWords.length;
  const bronzeCount = bronzeWords.length;

  // A finished past puzzle whose rare-word bonus hasn't been scored yet (the
  // scoredPlay isn't in the session — e.g. rollover still in progress, or a
  // transient fetch gap). rarityPoints is genuinely *unknown* here, not zero, so
  // show the same pending treatment as today rather than a misleading "0".
  const rarityPending = !props.puzzle.isToday && props.puzzle.played && rarityPoints === undefined;
  const rarityUnknown = props.puzzle.isToday || rarityPending;

  return (
    <div className="screen review">
      <div className="review__header">
        <AppBar
          onBack={onBackClicked}
          right={
            <button className="btn btn--secondary btn--sm" onClick={onShareClicked}>
              <ShareIcon />
              Share
            </button>
          }
        />
        <div className="review__top">
          <div className="review__sum">
            <div>
              <span className="eyebrow eyebrow--accent">Puzzle review</span>
              <div className="review__date">{longDate(props.puzzle.date)}</div>
            </div>
            <div className="review__score"></div>
          </div>

          <PuzzleReviewBoard puzzle={props.puzzle} selectedWord={selectedWord} />

          {!props.puzzle.isToday && (
            <div className="review__hint">Tap any word to spot it on the board</div>
          )}
        </div>
      </div>

      <div className="review__body">
        <div className="review__scorecard">
          <div className="scorecard__cells">
            <div className="scorecell">
              <div className="scorecell__val num">{(regularPoints ?? 0).toLocaleString()}</div>
              <div className="scorecell__lbl">Regular</div>
            </div>
            <div className="scorecell">
              {rarityUnknown ? (
                <div className="scorecell__val scorecell__unknown">
                  ???
                  {props.puzzle.isToday && (
                    <button
                      className="infoBtn"
                      onClick={() => dataLayer.openOverlay("rarity")}
                      aria-label="About rare-word bonuses"
                    >
                      <InfoIcon />
                    </button>
                  )}
                </div>
              ) : (
                <div className="scorecell__val num">{(rarityPoints ?? 0).toLocaleString()}</div>
              )}
              <div className="scorecell__lbl">Rare-word bonus</div>
            </div>
            <div className="scorecell scorecell--total">
              {rarityUnknown ? (
                <div className="scorecell__val scorecell__unknown">???</div>
              ) : (
                <div className="scorecell__val num">{totalScore.toLocaleString()}</div>
              )}
              <div className="scorecell__lbl">Total</div>
            </div>
          </div>
          {props.puzzle.isToday && (
            <div className="review__return">
              <b>Come back tomorrow.</b> Once today&rsquo;s puzzle closes we count how many players
              found each of your words, then reveal your rare-word bonuses and final score.
            </div>
          )}
          {rarityPending && (
            <div className="review__return">
              <b>Tallying the scores&hellip;</b> We&rsquo;re still counting how rare your words were
              &mdash; your rare-word bonus and final score will appear here shortly.
            </div>
          )}
        </div>

        {!props.puzzle.isToday && (
          <div className="review__medals">
            <span className="mchip">
              <span className="mdot g"></span>Gold {goldCount}
            </span>
            <span className="mchip">
              <span className="mdot s"></span>Silver {silverCount}
            </span>
            <span className="mchip">
              <span className="mdot b"></span>Bronze {bronzeCount}
            </span>
          </div>
        )}

        <ReviewRareWords
          puzzle={props.puzzle}
          selectedWord={selectedWord}
          goldWords={goldWords}
          silverWords={silverWords}
          bronzeWords={bronzeWords}
          setSelectedWord={setSelectedWord}
        />
        <ReviewAllWords
          puzzle={props.puzzle}
          wordsToDisplayByLength={wordsToDisplayByLength}
          foundWordsCount={foundWordsCount}
          foundCountByLength={foundCountByLength}
          selectedWord={selectedWord}
          setSelectedWord={setSelectedWord}
        />

        <div className="review__community">
          <div className="eyebrow">Community</div>
          <div>
            <b className="num">
              {props.puzzle.isToday ? "\u2014" : props.puzzle.totalPlays ?? "\u2014"}
            </b>
          </div>
          <div className="muted review__community-sub">
            {props.puzzle.isToday ? "Check back tomorrow!" : "players solved this puzzle"}
          </div>
        </div>
      </div>

      {showRarityInfo && (
        <div
          className="rarityInfo__scrim"
          role="dialog"
          aria-modal="true"
          aria-label="About rare-word bonuses"
          onClick={() => dataLayer.closeOverlay()}
        >
          <div className="rarityInfo__card" onClick={(e) => e.stopPropagation()}>
            <div className="rarityInfo__title">Rare-word bonuses</div>
            <p className="rarityInfo__body">
              The rarer a word, the more it&rsquo;s worth. We can only tell how rare each word is
              once today&rsquo;s puzzle closes and everyone&rsquo;s plays are counted&mdash;so your
              rare-word bonus and total score are revealed tomorrow.
            </p>
            <button className="btn btn--primary" onClick={() => dataLayer.closeOverlay()}>
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
